import type { PropsWithChildren, ReactNode } from "react";

import { Heading, Paper } from "@stacklet/ui";
import { graphql, useFragment } from "react-relay";
import { Outlet } from "react-router";

import { BreadCrumbsHeader } from "app/components/BreadCrumbsHeader";
import { HelpDrawer } from "app/components/HelpDrawer/HelpDrawer";
import { useToggleSearchParam } from "app/hooks/routing/useToggleSearchParam";
import { useTitle } from "app/hooks/ui/useTitle";

import type { PageWithBreadCrumbs_query$key } from "./__generated__/PageWithBreadCrumbs_query.graphql";
import type { BreadCrumbElement } from "app/components/BreadCrumbsHeader";

export interface PageWithBreadCrumbsProps extends PropsWithChildren {
  queryRef: PageWithBreadCrumbs_query$key;
  breadcrumbs?: BreadCrumbElement[];
  /** Prefix for the document/browser title, joined as `${defaultTitle} | ${title}`. */
  defaultTitle?: string;
  /** Page title: shown as the trailing breadcrumb and used to build the document title. */
  title?: string;
  /** Optional page heading (H2) rendered above the page content. */
  heading?: string;
  /** Optional description rendered under the heading. */
  subheading?: ReactNode;
  /** Constrain the paper's content to a fixed form width. */
  isForm?: boolean;
  /** Wrap content in a card-like Paper surface. */
  withPaper?: boolean;
  /** Help shown in the side panel while `?help=true`; pages without help omit it. */
  helpContent?: ReactNode;
}

export function PageWithBreadCrumbs({
  queryRef,
  defaultTitle,
  breadcrumbs,
  children,
  title = "",
  heading = "",
  subheading,
  isForm = false,
  withPaper = true,
  helpContent,
}: PageWithBreadCrumbsProps) {
  const data = useFragment(
    graphql`
      fragment PageWithBreadCrumbs_query on Query {
        ...BreadCrumbsHeader_query
      }
    `,
    queryRef,
  );
  const [isHelpOpen] = useToggleSearchParam("help");

  const documentTitle =
    defaultTitle && defaultTitle !== title
      ? `${defaultTitle} | ${title}`
      : title;
  useTitle(documentTitle);

  const content = (
    <>
      {heading ? (
        <div className="mb-xl flex flex-col gap-xs">
          <Heading level={2}>{heading}</Heading>
          {subheading ? <p className="text-body-medium">{subheading}</p> : null}
        </div>
      ) : null}
      {children}
    </>
  );

  return (
    <div className="grow">
      <Outlet />
      {breadcrumbs?.length ? (
        <BreadCrumbsHeader
          breadcrumbs={breadcrumbs}
          currentPageTitle={title}
          queryRef={data}
        />
      ) : null}

      <div className="flex items-start gap-x-xl">
        <div className="min-w-0 grow">
          {withPaper ? (
            <Paper>
              {isForm ? (
                <div className="w-full max-w-[700px]">{content}</div>
              ) : (
                content
              )}
            </Paper>
          ) : (
            content
          )}
        </div>
        {isHelpOpen && helpContent ? (
          <HelpDrawer>{helpContent}</HelpDrawer>
        ) : null}
      </div>
    </div>
  );
}
